import { useState, useEffect, useCallback } from "react";

const PROFESSIONS = {
  "Janitor": {
    salary: 1600,
    expenses: { taxes: 280, mortgage: 200, schoolLoan: 0, carLoan: 60, creditCard: 110, other: 300 },
    perChild: 70,
    savings: 560,
    liabilities: { homeMortgage: 20000, schoolLoans: 0, carLoans: 4000, creditCards: 3000 },
    color: "#6B7280"
  },
  "Truck Driver": {
    salary: 2500,
    expenses: { taxes: 460, mortgage: 400, schoolLoan: 0, carLoan: 80, creditCard: 110, other: 570 },
    perChild: 140,
    savings: 750,
    liabilities: { homeMortgage: 38000, schoolLoans: 0, carLoans: 4000, creditCards: 3000 },
    color: "#D97706"
  },
  "Secretary": {
    salary: 2500,
    expenses: { taxes: 460, mortgage: 400, schoolLoan: 0, carLoan: 80, creditCard: 110, other: 570 },
    perChild: 140,
    savings: 710,
    liabilities: { homeMortgage: 38000, schoolLoans: 0, carLoans: 4000, creditCards: 3000 },
    color: "#7C3AED"
  },
  "Police Officer": {
    salary: 3000,
    expenses: { taxes: 580, mortgage: 400, schoolLoan: 0, carLoan: 100, creditCard: 110, other: 690 },
    perChild: 160,
    savings: 520,
    liabilities: { homeMortgage: 46000, schoolLoans: 0, carLoans: 5000, creditCards: 3000 },
    color: "#1D4ED8"
  },
  "Mechanic": {
    salary: 2000,
    expenses: { taxes: 360, mortgage: 300, schoolLoan: 0, carLoan: 60, creditCard: 110, other: 450 },
    perChild: 110,
    savings: 670,
    liabilities: { homeMortgage: 31000, schoolLoans: 0, carLoans: 3000, creditCards: 3000 },
    color: "#92400E"
  },
  "Nurse": {
    salary: 3100,
    expenses: { taxes: 600, mortgage: 400, schoolLoan: 30, carLoan: 100, creditCard: 140, other: 710 },
    perChild: 170,
    savings: 480,
    liabilities: { homeMortgage: 47000, schoolLoans: 6000, carLoans: 5000, creditCards: 4000 },
    color: "#BE185D"
  },
  "Teacher (K-12)": {
    salary: 3300,
    expenses: { taxes: 630, mortgage: 500, schoolLoan: 60, carLoan: 100, creditCard: 140, other: 760 },
    perChild: 180,
    savings: 400,
    liabilities: { homeMortgage: 50000, schoolLoans: 12000, carLoans: 5000, creditCards: 4000 },
    color: "#059669"
  },
  "Business Manager": {
    salary: 4600,
    expenses: { taxes: 910, mortgage: 700, schoolLoan: 60, carLoan: 120, creditCard: 140, other: 1000 },
    perChild: 240,
    savings: 400,
    liabilities: { homeMortgage: 75000, schoolLoans: 12000, carLoans: 6000, creditCards: 4000 },
    color: "#0891B2"
  },
  "Engineer": {
    salary: 4900,
    expenses: { taxes: 1050, mortgage: 700, schoolLoan: 60, carLoan: 140, creditCard: 170, other: 1090 },
    perChild: 250,
    savings: 400,
    liabilities: { homeMortgage: 75000, schoolLoans: 12000, carLoans: 7000, creditCards: 5000 },
    color: "#2563EB"
  },
  "Airline Pilot": {
    salary: 9500,
    expenses: { taxes: 2350, mortgage: 1330, schoolLoan: 0, carLoan: 300, creditCard: 710, other: 2210 },
    perChild: 480,
    savings: 400,
    liabilities: { homeMortgage: 143000, schoolLoans: 0, carLoans: 15000, creditCards: 23000 },
    color: "#0F172A"
  },
  "Lawyer": {
    salary: 7500,
    expenses: { taxes: 1830, mortgage: 1100, schoolLoan: 390, carLoan: 220, creditCard: 230, other: 1650 },
    perChild: 380,
    savings: 400,
    liabilities: { homeMortgage: 115000, schoolLoans: 78000, carLoans: 11000, creditCards: 7000 },
    color: "#7C2D12"
  },
  "Doctor (MD)": {
    salary: 13200,
    expenses: { taxes: 3420, mortgage: 1900, schoolLoan: 750, carLoan: 380, creditCard: 320, other: 2880 },
    perChild: 640,
    savings: 400,
    liabilities: { homeMortgage: 202000, schoolLoans: 150000, carLoans: 19000, creditCards: 10000 },
    color: "#DC2626"
  }
};

const fmt = (n) => n < 0
  ? `-$${Math.abs(n).toLocaleString()}`
  : `$${n.toLocaleString()}`;

const PASSIVE_TYPES = { "Real Estate": "🏠", "Business": "🏢", "Dividends": "📈" };

export default function CashflowApp() {
  const [selected, setSelected] = useState(null);
  const [children, setChildren] = useState(0);
  const [bankLoan, setBankLoan] = useState(0);
  const [passiveIncome, setPassiveIncome] = useState([]);
  const [cashOnHand, setCashOnHand] = useState(0);
  const [newPassiveName, setNewPassiveName] = useState("");
  const [newPassiveAmt, setNewPassiveAmt] = useState("");
  const [newPassiveType, setNewPassiveType] = useState("Real Estate");
  const [showSelector, setShowSelector] = useState(true);
  const [paydayHistory, setPaydayHistory] = useState([]);
  const [roundCount, setRoundCount] = useState(0);
  const [showAddPassive, setShowAddPassive] = useState(false);
  const [animate, setAnimate] = useState(false);

  // Investment cards (bought deals)
  const [investments, setInvestments] = useState([]);
  const [showAddInvestment, setShowAddInvestment] = useState(false);
  const [newInvName, setNewInvName] = useState("");
  const [newInvType, setNewInvType] = useState("Real Estate");
  const [newInvCost, setNewInvCost] = useState(""); // down payment / cost
  const [newInvLiability, setNewInvLiability] = useState(""); // mortgage/loan taken on
  const [newInvCashflow, setNewInvCashflow] = useState(""); // monthly cashflow

  // Sell investment
  const [sellTarget, setSellTarget] = useState(null);
  const [sellPrice, setSellPrice] = useState("");

  // Doodads / Downsized
  const [showDoodad, setShowDoodad] = useState(false);
  const [doodadName, setDoodadName] = useState("");
  const [doodadAmt, setDoodadAmt] = useState("");
  const [doodadType, setDoodadType] = useState("Doodad");
  const [doodadLog, setDoodadLog] = useState([]);

  const prof = selected ? PROFESSIONS[selected] : null;

  const baseExpenses = prof ? (
    prof.expenses.taxes +
    prof.expenses.mortgage +
    prof.expenses.schoolLoan +
    prof.expenses.carLoan +
    prof.expenses.creditCard +
    prof.expenses.other +
    (children * prof.perChild) +
    (bankLoan / 1000) * 100
  ) : 0;

  const totalPassive = passiveIncome.reduce((s, p) => s + p.amount, 0);
  const totalIncome = prof ? prof.salary + totalPassive : 0;
  const totalExpenses = baseExpenses;
  const monthlyCF = totalIncome - totalExpenses;
  const isOutOfRatRace = totalPassive >= totalExpenses;
  const investmentLiabilities = investments.reduce((s, inv) => s + inv.liability, 0);
  const totalLiabilities = prof ? (
    prof.liabilities.homeMortgage + prof.liabilities.schoolLoans +
    prof.liabilities.carLoans + prof.liabilities.creditCards + bankLoan + investmentLiabilities
  ) : 0;

  const selectProfession = (name) => {
    setSelected(name);
    setChildren(0);
    setBankLoan(0);
    setPassiveIncome([]);
    setInvestments([]);
    setDoodadLog([]);
    setCashOnHand(PROFESSIONS[name].savings + (PROFESSIONS[name].salary - Object.values(PROFESSIONS[name].expenses).reduce((a,b)=>a+b,0)));
    setPaydayHistory([]);
    setRoundCount(0);
    setShowSelector(false);
    setAnimate(true);
    setTimeout(() => setAnimate(false), 600);
  };

  const collectPaycheck = () => {
    setCashOnHand(c => c + monthlyCF);
    setPaydayHistory(h => [{round: roundCount, cf: monthlyCF, balance: cashOnHand + monthlyCF}, ...h].slice(0, 10));
  };

  const endTurn = () => {
    setRoundCount(r => r + 1);
  };

  const takeLoan = (amt) => {
    setBankLoan(b => b + amt);
    setCashOnHand(c => c + amt);
  };

  const payLoan = (amt) => {
    const pay = Math.min(amt, bankLoan);
    if (cashOnHand >= pay) {
      setBankLoan(b => b - pay);
      setCashOnHand(c => c - pay);
    }
  };

  const addPassive = () => {
    const amt = parseInt(newPassiveAmt);
    if (!newPassiveName || !amt) return;
    setPassiveIncome(p => [...p, { id: Date.now(), name: newPassiveName, amount: amt, type: newPassiveType }]);
    setNewPassiveName("");
    setNewPassiveAmt("");
    setShowAddPassive(false);
  };

  const removePassive = (id) => setPassiveIncome(p => p.filter(x => x.id !== id));

  // Buy an investment card
  const buyInvestment = () => {
    const cost = parseInt(newInvCost) || 0;
    const liability = parseInt(newInvLiability) || 0;
    const cf = parseInt(newInvCashflow) || 0;
    if (!newInvName) return;
    if (cost > cashOnHand) return;
    const id = Date.now();
    setInvestments(inv => [...inv, { id, name: newInvName, type: newInvType, cost, liability, cashflow: cf }]);
    // Add to passive income if it generates cashflow
    if (cf > 0) {
      setPassiveIncome(p => [...p, { id, name: newInvName, amount: cf, type: newInvType }]);
    }
    setCashOnHand(c => c - cost);
    setNewInvName(""); setNewInvCost(""); setNewInvLiability(""); setNewInvCashflow("");
    setShowAddInvestment(false);
  };

  // Sell an investment card
  const confirmSell = () => {
    const price = parseInt(sellPrice) || 0;
    if (!sellTarget) return;
    const inv = investments.find(i => i.id === sellTarget);
    if (!inv) return;
    const profit = price - inv.liability;
    setCashOnHand(c => c + profit);
    setInvestments(i => i.filter(x => x.id !== sellTarget));
    setPassiveIncome(p => p.filter(x => x.id !== sellTarget));
    setSellTarget(null);
    setSellPrice("");
  };

  // Spend on doodad / downsized
  const spendDoodad = () => {
    const amt = parseInt(doodadAmt) || 0;
    if (!doodadName || !amt || cashOnHand < amt) return;
    setCashOnHand(c => c - amt);
    setDoodadLog(d => [{ id: Date.now(), name: doodadName, amount: amt, type: doodadType, round: roundCount }, ...d].slice(0, 20));
    setDoodadName(""); setDoodadAmt(""); setShowDoodad(false);
  };

  if (showSelector) {
    return (
      <div style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)",
        fontFamily: "'Georgia', serif",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "40px 20px"
      }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <div style={{ fontSize: 14, letterSpacing: 8, color: "#F59E0B", textTransform: "uppercase", marginBottom: 12 }}>
            CASHFLOW 101
          </div>
          <h1 style={{ fontSize: "clamp(32px, 6vw, 64px)", color: "#F8FAFC", margin: 0, fontWeight: 700, lineHeight: 1.1 }}>
            Choose Your<br /><span style={{ color: "#F59E0B" }}>Profession</span>
          </h1>
          <p style={{ color: "#94A3B8", marginTop: 16, fontSize: 15 }}>
            Your career defines your starting financial position. Choose wisely.
          </p>
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: 16,
          maxWidth: 1100,
          width: "100%"
        }}>
          {Object.entries(PROFESSIONS).map(([name, data]) => {
            const exp = Object.values(data.expenses).reduce((a,b)=>a+b,0);
            const cf = data.salary - exp;
            return (
              <button
                key={name}
                onClick={() => selectProfession(name)}
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: `1px solid rgba(255,255,255,0.1)`,
                  borderRadius: 16,
                  padding: "20px 24px",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "all 0.2s",
                  position: "relative",
                  overflow: "hidden"
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = `rgba(${hexToRgb(data.color)}, 0.15)`;
                  e.currentTarget.style.borderColor = data.color;
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <div style={{ position: "absolute", top: 0, right: 0, width: 80, height: 80, background: `${data.color}20`, borderRadius: "0 16px 0 80px" }} />
                <div style={{ color: data.color, fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", marginBottom: 8 }}>
                  {fmt(data.salary)}/mo
                </div>
                <div style={{ color: "#F8FAFC", fontSize: 20, fontWeight: 700, marginBottom: 12 }}>{name}</div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                  <span style={{ color: "#64748B" }}>Expenses</span>
                  <span style={{ color: "#EF4444" }}>{fmt(exp)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginTop: 4 }}>
                  <span style={{ color: "#64748B" }}>Monthly CF</span>
                  <span style={{ color: cf > 0 ? "#22C55E" : "#EF4444", fontWeight: 700 }}>{fmt(cf)}</span>
                </div>
                <div style={{ marginTop: 12, height: 3, background: "#1E293B", borderRadius: 2 }}>
                  <div style={{
                    width: `${Math.min(100, (cf / data.salary) * 100)}%`,
                    height: "100%",
                    background: data.color,
                    borderRadius: 2,
                    transition: "width 0.3s"
                  }} />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const expBreakdown = prof ? [
    { label: "Taxes", value: prof.expenses.taxes, color: "#EF4444" },
    { label: "Home Mortgage", value: prof.expenses.mortgage, color: "#F97316" },
    { label: "School Loan", value: prof.expenses.schoolLoan, color: "#EAB308" },
    { label: "Car Loan", value: prof.expenses.carLoan, color: "#84CC16" },
    { label: "Credit Card", value: prof.expenses.creditCard, color: "#06B6D4" },
    { label: "Other", value: prof.expenses.other, color: "#8B5CF6" },
    { label: `Children (×${children})`, value: children * prof.perChild, color: "#EC4899" },
    { label: "Bank Loan Interest", value: (bankLoan / 1000) * 100, color: "#94A3B8" },
  ].filter(e => e.value > 0) : [];

  const profColor = prof ? PROFESSIONS[selected].color : "#F59E0B";

  return (
    <div style={{
      minHeight: "100vh",
      background: "#0A0F1E",
      fontFamily: "'Georgia', serif",
      color: "#F1F5F9"
    }}>
      {/* Top bar */}
      <div style={{
        background: "rgba(255,255,255,0.03)",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        padding: "12px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 12
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button
            onClick={() => setShowSelector(true)}
            style={{
              background: "none", border: "1px solid rgba(255,255,255,0.15)",
              color: "#94A3B8", padding: "6px 14px", borderRadius: 8,
              cursor: "pointer", fontSize: 12, letterSpacing: 1
            }}
          >← PROFESSIONS</button>
          <div>
            <div style={{ fontSize: 10, color: "#64748B", letterSpacing: 3, textTransform: "uppercase" }}>Current Role</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: profColor }}>{selected}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          <Stat label="Cash on Hand" value={fmt(cashOnHand)} color="#F59E0B" big />
          <Stat label="Monthly CF" value={fmt(monthlyCF)} color={monthlyCF >= 0 ? "#22C55E" : "#EF4444"} big />
          <Stat label="Round" value={`#${roundCount}`} color="#94A3B8" big />
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            onClick={collectPaycheck}
            style={{
              background: `linear-gradient(135deg, ${profColor}, ${profColor}99)`,
              border: "none", color: "white", padding: "10px 24px",
              borderRadius: 10, cursor: "pointer", fontWeight: 700,
              fontSize: 14, letterSpacing: 1, fontFamily: "inherit"
            }}
          >
            💰 COLLECT PAYCHECK
          </button>
          <button
            onClick={() => setShowDoodad(true)}
            style={{
              background: "rgba(239,68,68,0.15)",
              border: "1px solid #EF444444", color: "#FCA5A5", padding: "10px 18px",
              borderRadius: 10, cursor: "pointer", fontWeight: 700,
              fontSize: 13, letterSpacing: 1, fontFamily: "inherit"
            }}
          >
            🛍️ DOODAD / DOWNSIZED
          </button>
          <button
            onClick={endTurn}
            style={{
              background: "rgba(100,116,139,0.2)",
              border: "2px solid rgba(100,116,139,0.5)", color: "#CBD5E1", padding: "10px 20px",
              borderRadius: 10, cursor: "pointer", fontWeight: 700,
              fontSize: 13, letterSpacing: 1, fontFamily: "inherit"
            }}
          >
            ⏭️ END TURN
          </button>
        </div>
      </div>

      {/* Rat Race status banner */}
      {isOutOfRatRace && (
        <div style={{
          background: "linear-gradient(135deg, #14532D, #166534)",
          borderBottom: "2px solid #22C55E",
          padding: "10px 24px",
          textAlign: "center",
          fontSize: 16,
          fontWeight: 700,
          color: "#4ADE80",
          letterSpacing: 2
        }}>
          🎉 CONGRATULATIONS! YOU'RE OUT OF THE RAT RACE! Passive Income ({fmt(totalPassive)}) &gt; Total Expenses ({fmt(totalExpenses)})
        </div>
      )}

      <div style={{ padding: "24px", maxWidth: 1400, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20 }}>

          {/* INCOME STATEMENT */}
          <div style={{ gridColumn: "1 / 3" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>

              {/* Income Card */}
              <Card title="① INCOME" accent="#22C55E">
                <IncomeRow label="Salary" value={prof.salary} color="#22C55E" bold />
                <IncomeRow label="Passive Income" value={totalPassive} color="#86EFAC" />
                <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", marginTop: 8, paddingTop: 8 }}>
                  <IncomeRow label="TOTAL INCOME" value={totalIncome} color="#22C55E" bold />
                </div>

                {/* Passive income list */}
                <div style={{ marginTop: 12 }}>
                  {passiveIncome.map(p => (
                    <div key={p.id} style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "6px 10px", background: "rgba(34,197,94,0.08)",
                      borderRadius: 8, marginBottom: 4, fontSize: 12
                    }}>
                      <span>{PASSIVE_TYPES[p.type]} {p.name}</span>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <span style={{ color: "#4ADE80", fontWeight: 700 }}>{fmt(p.amount)}</span>
                        <button onClick={() => removePassive(p.id)} style={{
                          background: "rgba(239,68,68,0.2)", border: "none", color: "#EF4444",
                          borderRadius: 4, cursor: "pointer", padding: "1px 6px", fontSize: 11
                        }}>✕</button>
                      </div>
                    </div>
                  ))}
                </div>

                {showAddPassive ? (
                  <div style={{ marginTop: 10, padding: 12, background: "rgba(255,255,255,0.05)", borderRadius: 10 }}>
                    <div style={{ fontSize: 11, color: "#64748B", marginBottom: 8, letterSpacing: 2 }}>ADD PASSIVE INCOME</div>
                    <select
                      value={newPassiveType}
                      onChange={e => setNewPassiveType(e.target.value)}
                      style={inputStyle}
                    >
                      {Object.keys(PASSIVE_TYPES).map(t => <option key={t}>{t}</option>)}
                    </select>
                    <input
                      placeholder="Name (e.g. Condo 2BR/1BA)"
                      value={newPassiveName}
                      onChange={e => setNewPassiveName(e.target.value)}
                      style={{ ...inputStyle, marginTop: 6 }}
                    />
                    <input
                      placeholder="Monthly cash flow ($)"
                      type="number"
                      value={newPassiveAmt}
                      onChange={e => setNewPassiveAmt(e.target.value)}
                      style={{ ...inputStyle, marginTop: 6 }}
                    />
                    <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                      <button onClick={addPassive} style={{ ...btnStyle, background: "#166534", flex: 1 }}>Add</button>
                      <button onClick={() => setShowAddPassive(false)} style={{ ...btnStyle, flex: 1 }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowAddPassive(true)}
                    style={{ ...btnStyle, marginTop: 10, width: "100%", borderStyle: "dashed" }}
                  >
                    + Add Passive Income
                  </button>
                )}
              </Card>

              {/* Expenses Card */}
              <Card title="② EXPENSES" accent="#EF4444">
                {expBreakdown.map((e, i) => (
                  <div key={i} style={{
                    display: "flex", justifyContent: "space-between",
                    padding: "5px 0", borderBottom: "1px solid rgba(255,255,255,0.04)",
                    fontSize: 13
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: e.color, flexShrink: 0 }} />
                      <span style={{ color: "#94A3B8" }}>{e.label}</span>
                    </div>
                    <span style={{ color: "#FCA5A5", fontWeight: 600 }}>{fmt(e.value)}</span>
                  </div>
                ))}

                {/* Children control */}
                <div style={{
                  marginTop: 12, padding: "10px 12px",
                  background: "rgba(236,72,153,0.08)", borderRadius: 10
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 11, color: "#64748B", letterSpacing: 2 }}>CHILDREN</div>
                      <div style={{ fontSize: 12, color: "#94A3B8" }}>+{fmt(prof.perChild)} each</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button onClick={() => setChildren(c => Math.max(0, c-1))} style={counterBtn}>−</button>
                      <span style={{ fontSize: 20, fontWeight: 700, minWidth: 24, textAlign: "center" }}>{children}</span>
                      <button onClick={() => setChildren(c => Math.min(3, c+1))} style={counterBtn}>+</button>
                    </div>
                  </div>
                </div>

                <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", marginTop: 12, paddingTop: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: 700 }}>TOTAL EXPENSES</span>
                    <span style={{ color: "#EF4444", fontWeight: 700, fontSize: 16 }}>{fmt(totalExpenses)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                    <span style={{ fontWeight: 700, color: "#F59E0B" }}>MONTHLY CASH FLOW</span>
                    <span style={{ color: monthlyCF >= 0 ? "#22C55E" : "#EF4444", fontWeight: 700, fontSize: 16 }}>{fmt(monthlyCF)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 12 }}>
                    <span style={{ color: "#64748B" }}>Per Child Expense</span>
                    <span style={{ color: "#CBD5E1" }}>{fmt(prof.perChild)}</span>
                  </div>
                </div>
              </Card>

            </div>

            {/* INVESTMENT DEALS */}
            <Card title="📊 INVESTMENT DEALS" accent="#A78BFA" style={{ marginTop: 20 }}>
              {investments.length === 0 && !showAddInvestment && (
                <div style={{ color: "#475569", fontSize: 13, padding: "8px 0" }}>No investments yet. Buy a deal card!</div>
              )}
              {investments.map(inv => (
                <div key={inv.id} style={{
                  padding: "10px 12px", background: "rgba(167,139,250,0.08)",
                  borderRadius: 10, marginBottom: 8, border: "1px solid rgba(167,139,250,0.15)"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#C4B5FD" }}>
                        {PASSIVE_TYPES[inv.type] || "📊"} {inv.name}
                      </div>
                      <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>
                        Cost: {fmt(inv.cost)} · Liability: {fmt(inv.liability)} · CF: <span style={{ color: inv.cashflow >= 0 ? "#4ADE80" : "#EF4444" }}>{fmt(inv.cashflow)}/mo</span>
                      </div>
                    </div>
                    <button
                      onClick={() => { setSellTarget(inv.id); setSellPrice(""); }}
                      style={{
                        background: "rgba(251,191,36,0.15)", border: "1px solid #F59E0B44",
                        color: "#FCD34D", borderRadius: 6, cursor: "pointer",
                        padding: "4px 10px", fontSize: 11, fontFamily: "inherit"
                      }}
                    >SELL</button>
                  </div>
                  {sellTarget === inv.id && (
                    <div style={{ marginTop: 8, padding: 10, background: "rgba(255,255,255,0.04)", borderRadius: 8 }}>
                      <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 6 }}>
                        Sell for: <span style={{ color: "#FCD34D" }}>Profit = Sale Price − Liability ({fmt(inv.liability)})</span>
                      </div>
                      <input
                        placeholder="Sale price ($)"
                        type="number"
                        value={sellPrice}
                        onChange={e => setSellPrice(e.target.value)}
                        style={{ ...inputStyle, marginBottom: 6 }}
                      />
                      {sellPrice && (
                        <div style={{ fontSize: 12, color: parseInt(sellPrice) - inv.liability >= 0 ? "#4ADE80" : "#EF4444", marginBottom: 6 }}>
                          Profit: {fmt(parseInt(sellPrice) - inv.liability)}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={confirmSell} style={{ ...btnStyle, flex: 1, background: "#166534", color: "#4ADE80", borderColor: "#22C55E44" }}>Confirm Sale</button>
                        <button onClick={() => setSellTarget(null)} style={{ ...btnStyle, flex: 1 }}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {showAddInvestment ? (
                <div style={{ marginTop: 8, padding: 14, background: "rgba(255,255,255,0.05)", borderRadius: 10 }}>
                  <div style={{ fontSize: 11, color: "#64748B", marginBottom: 10, letterSpacing: 2 }}>BUY INVESTMENT / DEAL CARD</div>
                  <select value={newInvType} onChange={e => setNewInvType(e.target.value)} style={inputStyle}>
                    {Object.keys(PASSIVE_TYPES).map(t => <option key={t}>{t}</option>)}
                    <option>Stocks</option>
                  </select>
                  <input placeholder="Name (e.g. 3BR/2BA Condo)" value={newInvName} onChange={e => setNewInvName(e.target.value)} style={{ ...inputStyle, marginTop: 6 }} />
                  <input placeholder="Down payment / cost out of pocket ($)" type="number" value={newInvCost} onChange={e => setNewInvCost(e.target.value)} style={{ ...inputStyle, marginTop: 6 }} />
                  <input placeholder="Mortgage / liability taken on ($, 0 if none)" type="number" value={newInvLiability} onChange={e => setNewInvLiability(e.target.value)} style={{ ...inputStyle, marginTop: 6 }} />
                  <input placeholder="Monthly cash flow (+ positive, - negative)" type="number" value={newInvCashflow} onChange={e => setNewInvCashflow(e.target.value)} style={{ ...inputStyle, marginTop: 6 }} />
                  {newInvCost && parseInt(newInvCost) > cashOnHand && (
                    <div style={{ color: "#EF4444", fontSize: 12, marginTop: 6 }}>⚠️ Not enough cash on hand ({fmt(cashOnHand)})</div>
                  )}
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button onClick={buyInvestment} disabled={parseInt(newInvCost) > cashOnHand} style={{ ...btnStyle, flex: 1, background: "#1E3A5F", color: "#93C5FD", borderColor: "#3B82F644" }}>Buy</button>
                    <button onClick={() => setShowAddInvestment(false)} style={{ ...btnStyle, flex: 1 }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowAddInvestment(true)} style={{ ...btnStyle, width: "100%", borderStyle: "dashed", marginTop: 8 }}>
                  + Buy Deal / Investment Card
                </button>
              )}
            </Card>

            {/* BANK LOAN Section */}
            <Card title="🏦 BANK LOAN" accent="#F59E0B" style={{ marginTop: 20 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 11, color: "#64748B", letterSpacing: 2 }}>OUTSTANDING LOAN</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: "#F59E0B" }}>{fmt(bankLoan)}</div>
                  <div style={{ fontSize: 12, color: "#94A3B8" }}>Interest: {fmt((bankLoan / 1000) * 100)}/mo</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#64748B", letterSpacing: 2, marginBottom: 8 }}>BORROW</div>
                  {[1000, 2000, 5000, 10000].map(amt => (
                    <button key={amt} onClick={() => takeLoan(amt)} style={{
                      ...btnStyle, marginBottom: 4, width: "100%",
                      background: "rgba(245,158,11,0.1)", borderColor: "#F59E0B44",
                      color: "#FCD34D", fontSize: 12
                    }}>
                      +{fmt(amt)}
                    </button>
                  ))}
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#64748B", letterSpacing: 2, marginBottom: 8 }}>REPAY</div>
                  {[1000, 2000, 5000, bankLoan].filter((v,i,a) => v > 0 && a.indexOf(v) === i).map(amt => (
                    <button key={amt} onClick={() => payLoan(amt)} disabled={cashOnHand < amt || bankLoan < amt} style={{
                      ...btnStyle, marginBottom: 4, width: "100%",
                      background: cashOnHand >= amt && bankLoan >= amt ? "rgba(34,197,94,0.1)" : "rgba(255,255,255,0.03)",
                      borderColor: cashOnHand >= amt && bankLoan >= amt ? "#22C55E44" : "rgba(255,255,255,0.1)",
                      color: cashOnHand >= amt && bankLoan >= amt ? "#4ADE80" : "#475569",
                      fontSize: 12,
                      cursor: cashOnHand >= amt && bankLoan >= amt ? "pointer" : "not-allowed"
                    }}>
                      {amt === bankLoan && amt > 0 ? `Pay All ${fmt(amt)}` : `-${fmt(amt)}`}
                    </button>
                  ))}
                  {bankLoan === 0 && <div style={{ color: "#475569", fontSize: 12, padding: "8px 0" }}>No loan outstanding</div>}
                </div>
              </div>
              <div style={{
                marginTop: 12, padding: "8px 12px",
                background: "rgba(245,158,11,0.06)",
                borderRadius: 8, fontSize: 12, color: "#94A3B8"
              }}>
                💡 Every $1,000 borrowed = $100/month interest added to expenses. 10% monthly rate, repayable in $1,000 increments.
              </div>
            </Card>
          </div>

          {/* RIGHT COLUMN */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* Rat Race Status */}
            <Card title="🐀 RAT RACE STATUS" accent={isOutOfRatRace ? "#22C55E" : profColor}>
              <div style={{
                padding: "16px",
                background: isOutOfRatRace ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.08)",
                borderRadius: 10,
                border: `1px solid ${isOutOfRatRace ? "#22C55E44" : "#EF444422"}`
              }}>
                <div style={{ fontSize: 13, color: "#94A3B8", marginBottom: 8 }}>
                  To escape: Passive Income &gt; Total Expenses
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 13 }}>Passive Income</span>
                  <span style={{ color: "#22C55E", fontWeight: 700 }}>{fmt(totalPassive)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                  <span style={{ fontSize: 13 }}>Total Expenses</span>
                  <span style={{ color: "#EF4444", fontWeight: 700 }}>{fmt(totalExpenses)}</span>
                </div>
                <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 6, height: 10, overflow: "hidden" }}>
                  <div style={{
                    width: `${Math.min(100, totalExpenses > 0 ? (totalPassive / totalExpenses * 100) : 0)}%`,
                    height: "100%",
                    background: isOutOfRatRace
                      ? "linear-gradient(90deg, #22C55E, #4ADE80)"
                      : "linear-gradient(90deg, #EF4444, #F59E0B)",
                    transition: "width 0.5s",
                    borderRadius: 6
                  }} />
                </div>
                <div style={{ fontSize: 12, color: "#64748B", marginTop: 6, textAlign: "right" }}>
                  {totalExpenses > 0 ? Math.round(totalPassive / totalExpenses * 100) : 0}% covered
                </div>
                <div style={{ fontSize: 13, marginTop: 8, fontWeight: 600 }}>
                  Still need:{" "}
                  <span style={{ color: isOutOfRatRace ? "#22C55E" : "#EF4444" }}>
                    {isOutOfRatRace ? "✅ FREE!" : fmt(Math.max(0, totalExpenses - totalPassive)) + " more"}
                  </span>
                </div>
              </div>
            </Card>

            {/* Assets / Liabilities */}
            <Card title="③ ASSETS & ④ LIABILITIES" accent="#60A5FA">
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: "#64748B", letterSpacing: 2, marginBottom: 6 }}>ASSETS</div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 13 }}>
                  <span style={{ color: "#94A3B8" }}>Savings (starting)</span>
                  <span style={{ color: "#60A5FA" }}>{fmt(prof.savings)}</span>
                </div>
                {passiveIncome.map(p => (
                  <div key={p.id} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 12 }}>
                    <span style={{ color: "#64748B" }}>{PASSIVE_TYPES[p.type]} {p.name}</span>
                    <span style={{ color: "#93C5FD" }}>CF: {fmt(p.amount)}/mo</span>
                  </div>
                ))}
              </div>
              <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 12 }}>
                <div style={{ fontSize: 11, color: "#64748B", letterSpacing: 2, marginBottom: 6 }}>LIABILITIES</div>
                {[
                  { label: "Home Mortgage", value: prof.liabilities.homeMortgage },
                  { label: "School Loans", value: prof.liabilities.schoolLoans },
                  { label: "Car Loans", value: prof.liabilities.carLoans },
                  { label: "Credit Cards", value: prof.liabilities.creditCards },
                  { label: "Bank Loan", value: bankLoan },
                  ...investments.filter(i => i.liability > 0).map(i => ({ label: `${i.name} Mortgage`, value: i.liability })),
                ].filter(l => l.value > 0).map((l, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 13, borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                    <span style={{ color: "#94A3B8" }}>{l.label}</span>
                    <span style={{ color: "#FCA5A5" }}>{fmt(l.value)}</span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8, fontWeight: 700 }}>
                  <span>Total Liabilities</span>
                  <span style={{ color: "#EF4444" }}>{fmt(totalLiabilities)}</span>
                </div>
              </div>
            </Card>

            {/* Payday Log */}
            {paydayHistory.length > 0 && (
              <Card title="📋 PAYDAY LOG" accent="#8B5CF6">
                {paydayHistory.map((h, i) => (
                  <div key={i} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.04)",
                    fontSize: 12
                  }}>
                    <span style={{ color: "#64748B" }}>Round {h.round}</span>
                    <span style={{ color: h.cf >= 0 ? "#4ADE80" : "#EF4444" }}>{fmt(h.cf)}</span>
                    <span style={{ color: "#94A3B8" }}>→ {fmt(h.balance)}</span>
                  </div>
                ))}
              </Card>
            )}
            {/* Doodad Log */}
            {doodadLog.length > 0 && (
              <Card title="🛍️ DOODADS & SETBACKS" accent="#EF4444">
                {doodadLog.map((d) => (
                  <div key={d.id} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.04)",
                    fontSize: 12
                  }}>
                    <span style={{ color: d.type === "Downsized" ? "#F97316" : "#94A3B8" }}>
                      {d.type === "Downsized" ? "⬇️" : "🛍️"} {d.name}
                    </span>
                    <span style={{ color: "#64748B", fontSize: 11 }}>Rd {d.round}</span>
                    <span style={{ color: "#EF4444", fontWeight: 700 }}>−{fmt(d.amount)}</span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8, fontSize: 12, fontWeight: 700 }}>
                  <span style={{ color: "#64748B" }}>Total Spent</span>
                  <span style={{ color: "#EF4444" }}>{fmt(doodadLog.reduce((s, d) => s + d.amount, 0))}</span>
                </div>
              </Card>
            )}
          </div>

        </div>
      </div>

      {/* DOODAD / DOWNSIZED MODAL */}
      {showDoodad && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1000
        }}>
          <div style={{
            background: "#0F172A", border: "1px solid rgba(239,68,68,0.4)",
            borderRadius: 16, padding: 28, width: 360, boxShadow: "0 25px 60px rgba(0,0,0,0.6)"
          }}>
            <div style={{ fontSize: 11, letterSpacing: 3, color: "#EF4444", marginBottom: 4 }}>SPEND CASH</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#F8FAFC", marginBottom: 20 }}>
              🛍️ Doodad / Downsized
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: "#64748B", letterSpacing: 2, marginBottom: 6 }}>TYPE</div>
              <div style={{ display: "flex", gap: 8 }}>
                {["Doodad", "Downsized", "Other Expense"].map(t => (
                  <button key={t} onClick={() => setDoodadType(t)} style={{
                    flex: 1, padding: "8px 4px", borderRadius: 8, fontSize: 11,
                    cursor: "pointer", fontFamily: "inherit",
                    background: doodadType === t ? "rgba(239,68,68,0.2)" : "rgba(255,255,255,0.05)",
                    border: `1px solid ${doodadType === t ? "#EF4444" : "rgba(255,255,255,0.1)"}`,
                    color: doodadType === t ? "#FCA5A5" : "#64748B"
                  }}>{t}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: "#64748B", letterSpacing: 2, marginBottom: 6 }}>DESCRIPTION</div>
              <input
                placeholder={doodadType === "Downsized" ? "e.g. Lost job for 2 months" : "e.g. Boat, vacation, new TV"}
                value={doodadName}
                onChange={e => setDoodadName(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "#64748B", letterSpacing: 2, marginBottom: 6 }}>AMOUNT TO PAY ($)</div>
              <input
                placeholder="Amount"
                type="number"
                value={doodadAmt}
                onChange={e => setDoodadAmt(e.target.value)}
                style={inputStyle}
              />
              <div style={{ fontSize: 12, color: "#64748B", marginTop: 6 }}>
                Cash on hand: <span style={{ color: "#F59E0B" }}>{fmt(cashOnHand)}</span>
              </div>
              {doodadAmt && parseInt(doodadAmt) > cashOnHand && (
                <div style={{ color: "#EF4444", fontSize: 12, marginTop: 4 }}>
                  ⚠️ Not enough cash! Borrow from bank first.
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={spendDoodad}
                disabled={!doodadAmt || parseInt(doodadAmt) > cashOnHand || !doodadName}
                style={{
                  flex: 1, padding: "11px 0", borderRadius: 10, border: "none",
                  background: "linear-gradient(135deg, #7F1D1D, #EF4444)",
                  color: "white", fontWeight: 700, fontSize: 14, cursor: "pointer",
                  fontFamily: "inherit", opacity: (!doodadAmt || parseInt(doodadAmt) > cashOnHand || !doodadName) ? 0.5 : 1
                }}
              >PAY {doodadAmt ? fmt(parseInt(doodadAmt)) : ""}</button>
              <button onClick={() => { setShowDoodad(false); setDoodadName(""); setDoodadAmt(""); }} style={{ ...btnStyle, padding: "11px 20px" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ title, accent, children, style = {} }) {
  return (
    <div style={{
      background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 16,
      padding: 20,
      ...style
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, letterSpacing: 3,
        color: accent, textTransform: "uppercase", marginBottom: 14,
        borderBottom: `1px solid ${accent}33`, paddingBottom: 8
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function IncomeRow({ label, value, color, bold }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between",
      padding: "5px 0", borderBottom: "1px solid rgba(255,255,255,0.04)",
      fontSize: 13
    }}>
      <span style={{ color: "#94A3B8" }}>{label}</span>
      <span style={{ color, fontWeight: bold ? 700 : 500 }}>{fmt(value)}</span>
    </div>
  );
}

function Stat({ label, value, color, big }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: "#475569", letterSpacing: 2, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: big ? 20 : 14, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : "100, 100, 100";
}

const inputStyle = {
  width: "100%", padding: "8px 10px",
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 8, color: "#F1F5F9",
  fontSize: 12, fontFamily: "inherit",
  boxSizing: "border-box"
};

const btnStyle = {
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  color: "#94A3B8", padding: "8px 12px",
  borderRadius: 8, cursor: "pointer",
  fontSize: 13, fontFamily: "inherit",
  transition: "all 0.15s"
};

const counterBtn = {
  background: "rgba(255,255,255,0.08)",
  border: "1px solid rgba(255,255,255,0.15)",
  color: "#F1F5F9", width: 32, height: 32,
  borderRadius: 8, cursor: "pointer",
  fontSize: 18, display: "flex",
  alignItems: "center", justifyContent: "center",
  fontFamily: "inherit"
};
